"use client";

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { Link } from '@/i18n/routing';
import { useTranslations } from 'next-intl';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import XIcon from '@mui/icons-material/X';
import IconButton from '@mui/material/IconButton';
import Image from 'next/image';

const Footer = () => {
    const [currentYear, setCurrentYear] = React.useState<number | null>(null);
    const t = useTranslations('Footer');

    const sectionTitle = {
        fontWeight: 700,
        color: '#132c4b', // Brand Deep Blue
        mb: 3
    }

    const officeTitle = {
        fontWeight: 600,
        color: '#334155',
        mb: 1
    }

    const officeDesc = {
        color: '#64748b',
        fontSize: '14px',
        mt: 0.5
    }

    React.useEffect(() => {
        setCurrentYear(new Date().getFullYear());
    }, []);


    const FooterLink = ({ children, href = "#" }: { children: React.ReactNode, href?: string }) => {
        const isExternal = href.startsWith('http');
        return (
            <Link
                href={href}
                style={{ textDecoration: 'none' }}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noopener noreferrer" : undefined}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color: '#64748b',
                        textDecoration: 'none',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            color: '#F15859', // Brand Accent Red
                            transform: 'translateX(4px)'
                        }
                    }}
                >
                    {children}
                </Typography>
            </Link>
        );
    };

    return (
        <Box
            sx={{
                bgcolor: 'transparent',
                background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
                pt: { xs: 8, md: 10 },
                pb: 6,
                borderTop: '1px solid #e2e8f0'
            }}
        >
            <Box sx={{ maxWidth: 'lg', mx: 'auto', px: { xs: 2.5, md: 0 } }}>
                <Grid container spacing={6}>
                    {/* Brand */}
                    <Grid size={{ xs: 12 }}>
                        <Box sx={{ maxWidth: 500 }}>
                            <Link href="/" style={{ textDecoration: 'none' }}>
                                <Image
                                    src="/images/og-logo-color.webp"
                                    alt="AirXpress Logo"
                                    width={180}
                                    height={40}
                                    style={{ width: "auto", height: "40px" }}
                                />
                            </Link>

                            <Typography
                                variant="body1"
                                sx={{
                                    color: '#64748b',
                                    lineHeight: 1.8,
                                    mt: 2
                                }}
                            >
                                <strong>{t('brand_catchphrase')}</strong>
                                <br />
                                {t('brand_description')}
                            </Typography>

                            {/* Socials */}
                            <Box sx={{ display: 'flex', gap: 1.5, mt: 3, alignItems: 'center' }}>
                                {[
                                    { icon: FacebookIcon, href: 'https://www.facebook.com/spacianet.co.jp/' },
                                    { icon: InstagramIcon, href: 'https://www.instagram.com/spacianet__airxpress?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==' },
                                    { icon: XIcon, href: 'https://x.com/nairxpress' },
                                    {
                                        icon: (props: React.SVGProps<SVGSVGElement>) => (
                                            <svg
                                                viewBox="0 0 50 50"
                                                width="20px"
                                                height="20px"
                                                fill="currentColor"
                                                {...props}
                                            >
                                                <path d="M41,4H9C6.243,4,4,6.243,4,9v32c0,2.757,2.243,5,5,5h32c2.757,0,5-2.243,5-5V9C46,6.243,43.757,4,41,4z M37.006,22.323 c-0.227,0.021-0.457,0.035-0.69,0.035c-2.623,0-4.928-1.349-6.269-3.388c0,5.349,0,11.435,0,11.537c0,4.709-3.818,8.527-8.527,8.527 s-8.527-3.818-8.527-8.527s3.818-8.527,8.527-8.527c0.178,0,0.352,0.016,0.527,0.027v4.202c-0.175-0.021-0.347-0.053-0.527-0.053 c-2.404,0-4.352,1.948-4.352,4.352s1.948,4.352,4.352,4.352s4.527-1.894,4.527-4.298c0-0.095,0.042-19.594,0.042-19.594h4.016 c0.378,3.591,3.277,6.425,6.901,6.685V22.323z" />
                                            </svg>
                                        ),
                                        href: 'https://www.tiktok.com/@spacianet__airxpress' // Placeholder logic
                                    }
                                ].map(({ icon: Icon, href }, i) => (
                                    <IconButton
                                        key={i}
                                        component="a"
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        size="small"
                                        sx={{
                                            color: '#64748b',
                                            border: '1px solid #e2e8f0',
                                            bgcolor: '#ffffff',
                                            transition: 'all 0.25s ease',
                                            '&:hover': {
                                                color: '#F15859',
                                                borderColor: '#F15859',
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 6px 20px rgba(225, 29, 72, 0.15)'
                                            }
                                        }}
                                        aria-label={`Visit our ${i === 0 ? 'Facebook' : i === 1 ? 'Instagram' : i === 2 ? 'X (Twitter)' : 'TikTok'} page`}
                                    >
                                        <Icon fontSize="small" />
                                    </IconButton>
                                ))}
                            </Box>
                        </Box>
                    </Grid>

                    {/* Links */}
                    <Grid size={{ xs: 12 }}>
                        <Grid
                            container
                            spacing={4}
                            sx={{
                                borderTop: '1px solid #f1f5f9',
                                pt: { xs: 5, md: 6 }
                            }}
                        >
                            {/* Offices */}
                            <Grid size={{ xs: 12, md: 5 }}>
                                <Typography sx={sectionTitle}>{t('offices_title')}</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                    {[
                                        {
                                            title: t('office_1_title'),
                                            lines: [t('office_1_line1'), t('office_1_line2')]
                                        },
                                        {
                                            title: t('office_2_title'),
                                            lines: [t('office_2_line1'), t('office_2_line2')]
                                        },
                                        {
                                            title: t('office_3_title'),
                                            lines: [t('office_3_line1'), t('office_3_line2')]
                                        }
                                    ].map((office) => (
                                        <Box key={office.title}>
                                            <Typography sx={officeTitle}>{office.title}</Typography>
                                            {
                                                office.lines.map((item, index) => (
                                                    <Typography key={index} sx={officeDesc}>{item}</Typography>
                                                ))
                                            }
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>

                            {/* Quick Links */}
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Typography sx={sectionTitle}>{t('links_title')}</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.8 }}>
                                    {[
                                        { label: t('link_service'), href: '/service' },
                                        { label: t('link_ai_agent'), href: '/ai-agent' },
                                        { label: t('link_case_studies'), href: '/case-studies' },
                                        { label: t('link_faq'), href: '/faq' },
                                        { label: t('link_contact'), href: '/contact' }
                                    ].map((link) => (
                                        <FooterLink key={link.label} href={link.href}>
                                            {link.label}
                                        </FooterLink>
                                    ))}
                                </Box>
                            </Grid>

                            {/* Legal */}
                            <Grid size={{ xs: 6, md: 4 }}>
                                <Typography sx={sectionTitle}>{t('legal_title')}</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.8 }}>
                                    {[
                                        { label: t('link_csr'), href: '/csr' },
                                        { label: t('link_recruitment'), href: 'https://www.spacianet.co.jp/recruitment' },
                                        { label: t('link_sct'), href: '/specified-commercial-transaction' },
                                        { label: t('link_privacy'), href: 'https://www.spacianet.co.jp/プライバシーポリシー' }
                                    ].map((link) => (
                                        <FooterLink key={link.label} href={link.href}>
                                            {link.label}
                                        </FooterLink>
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Copyright */}
                <Box
                    sx={{
                        borderTop: '1px solid #e2e8f0',
                        mt: 8,
                        pt: 4,
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        © {currentYear ?? ""} SpaciaNet Japan Inc. All rights reserved.
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default Footer;
